import { ref } from "vue";
import { updatePrettyPreviewApi } from "@/api/previewRequests";
import { useModal } from "@/components/UI/Modal/useModal";
import { PRETTY_PREVIEW_CLOSED_DEAL, PRETTY_PREVIEW_STATUSES } from "@/consts/prettyPreviewValues";
import { requestPreviewRequestsRefresh } from "@/hooks/usePreviewRequestsRefresh";
import useToast from "@/hooks/useToast";
import { prettyPreviewCanvasUrl } from "@/utils/prettyPreview";

// Closed Deal is set by Sync, together with the order it creates — never by hand.
const EDITABLE_STATUSES = PRETTY_PREVIEW_STATUSES.filter(
  (status) => status.value !== PRETTY_PREVIEW_CLOSED_DEAL
);

// Editing a preview request that no order was created from yet: its client, its status
// and its canvas. One save sends it all.
export const useEditPrettyPreview = (preview) => {
  const { close } = useModal();
  const { openToast } = useToast();

  const values = ref({
    customer_name: preview.customer_name || "",
    phone: preview.phone || "",
    email: preview.email || "",
    status: preview.status || "open",
  });

  const canvas = {
    url: prettyPreviewCanvasUrl(preview),
    token: preview.canvas_token || "",
    scene: preview.canvas_scene || "",
  };

  // Bound to PreviewCanvas; `changes` is what it reports the user changed so far.
  const canvasRef = ref(null);
  const changes = ref([]);
  const isSaving = ref(false);

  const save = async (formValues) => {
    isSaving.value = true;
    const payload = {
      customer_name: formValues.customer_name,
      phone: formValues.phone,
      email: formValues.email,
      status: formValues.status,
    };

    if (changes.value.length) {
      try {
        const captured = await canvasRef.value.capture();
        payload.canvas_scene = captured.scene ? JSON.stringify(captured.scene) : "";
        payload.preview_image = captured.screenshot;
      } catch (error) {
        openToast("Failed to capture the preview from the canvas. Please try again.", "danger");
        isSaving.value = false;
        return;
      }
    }

    try {
      await updatePrettyPreviewApi(preview.id, payload);
      openToast("Preview request saved", "success");
      close();
      requestPreviewRequestsRefresh();
    } catch (error) {
      // The request layer has already toasted the server's message.
    } finally {
      isSaving.value = false;
    }
  };

  return {
    values,
    canvas,
    canvasRef,
    changes,
    isSaving,
    statusOptions: EDITABLE_STATUSES,
    save,
  };
};
